import React, { forwardRef, useRef } from 'react';
import { TextField } from '@mui/material';

import { tokenizePromptPlaceholders } from '../../../common/promptPlaceholders.js';

// Renders the prompt with its {{placeholders}} marked up. Placeholders may only be given
// colours here: any padding, border or font-weight would change glyph positions, and this
// layer has to line up character for character with the textarea drawn on top of it.
export const PromptHighlight = forwardRef(function PromptHighlight(
    // As an overlay this layer is decorative and must stay hidden from assistive tech (the
    // textarea underneath carries the text); rendered on its own it is the only copy.
    { value, className = 'prompt-highlight', ariaHidden = true },
    ref,
) {
    return (
        <pre className={className} aria-hidden={ariaHidden || undefined} ref={ref}>
            {tokenizePromptPlaceholders(value).map((token, index) => (
                token.type === 'placeholder' ? (
                    <mark className="prompt-placeholder" key={index}>{token.value}</mark>
                ) : (
                    <React.Fragment key={index}>{token.value}</React.Fragment>
                )
            ))}
            {/* A textarea keeps a line after a trailing newline; a <pre> collapses it. */}
            {'\n'}
        </pre>
    );
});

// A syntax-highlighted prompt field: the highlight layer sits behind a textarea whose own
// text is transparent, so editing behaves exactly like a normal MUI multiline field.
export default function PromptEditor({
    label,
    value,
    onChange,
    disabled = false,
    minRows = 3,
    maxRows,
    helperText,
    autoFocus = false,
}) {
    const highlightRef = useRef(null);

    const syncHighlightScroll = (event) => {
        const highlight = highlightRef.current;
        if (!highlight) return;
        highlight.scrollTop = event.target.scrollTop;
        highlight.scrollLeft = event.target.scrollLeft;
    };

    return (
        <TextField
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            helperText={helperText}
            size="small"
            fullWidth
            multiline
            minRows={minRows}
            maxRows={maxRows}
            autoFocus={autoFocus}
            InputProps={{
                className: 'prompt-editor-input',
                startAdornment: <PromptHighlight value={value} ref={highlightRef} />,
            }}
            // React 17+ does not bubble scroll, so this has to sit on the textarea itself.
            inputProps={{ onScroll: syncHighlightScroll }}
        />
    );
}
